import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { deriveDetailSections } from '../utils/detailSections';
import { matchAnyCI } from '../utils/search';

const formatNumber = (val) => { const n = String(val).replace(/[^0-9]/g,''); return n ? Number(n).toLocaleString() : ''; };
const parseNumber = (val) => parseInt(String(val).replace(/[^0-9]/g,'')) || 0;

const TYPE_LABEL = { software:'Software', license:'License', hardware:'Hardware', appliance:'Appliance' };
const TYPE_BADGE = { software:'badge-blue', license:'badge-purple', hardware:'badge-gray', appliance:'badge-green' };

export default function ProductNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEdit = !!editId;

  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [allParts, setAllParts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);  // Appliance 자식 후보용
  const [saving, setSaving] = useState(false);

  // 유형
  const [productType, setProductType] = useState('appliance');

  // 기본정보
  const [info, setInfo] = useState({ category:'', name:'', model:'', alias:'', status:'active' });

  // 상품구성 — Hardware 의 BOM
  const [bom, setBom] = useState([]);
  const [showAddPart, setShowAddPart] = useState(false);
  const [partSearch, setPartSearch] = useState('');

  // 상품구성 — Appliance 의 자식 components
  const [components, setComponents] = useState([]); // [{ child_product_id, quantity, _meta:{name,model,product_type,...} }]
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [compSearch, setCompSearch] = useState('');
  const [compTypeFilter, setCompTypeFilter] = useState('all');

  // 세부사양 — performance(성능사양 key-value) 는 그대로, sections 는 자유 입력 {title, content} 리스트
  const [specs, setSpecs] = useState({ performance:[], sections:[] });

  // 가격
  const [pricing, setPricing] = useState({ consumer_price:'', price_end_user:'', price_distributor:'', price_partner:'' });
  const [priceOverride, setPriceOverride] = useState(false);

  const [totalCost, setTotalCost] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/settings/codes/product_category'),
      api.get('/settings/codes/product_status'),
      api.get('/parts'),
      api.get('/products'),
    ]).then(([cats, sts, pts, prods]) => {
      setCategories(cats); setStatuses(sts); setAllParts(pts); setAllProducts(prods);
    });
  }, []);

  // 편집 로드
  useEffect(() => {
    if (!editId) return;
    api.get(`/products/${editId}`).then(p => {
      setProductType(p.product_type || 'appliance');
      setInfo({ category: p.category||'', name: p.name||'', model: p.model||'', alias: p.alias||'', status: p.status||'active' });
      setBom((p.bom||[]).map(b => ({ part_id: b.part_id, part_code: b.part_code, part_type: b.part_type, part_name: b.part_name, part_spec: b.part_spec, part_spec_display: b.part_spec_display, quantity: b.quantity, show_in_spec: b.show_in_spec, purchase_price: b.purchase_price })));
      setComponents((p.components||[]).map(c => ({
        child_product_id: c.child_product_id,
        quantity: c.quantity || 1,
        _meta: {
          name: c.name, model: c.model, alias: c.alias, product_type: c.product_type,
          main_title: c.main_title, sub_description: c.sub_description, sw_name: c.sw_name,
          consumer_price: c.consumer_price, total_cost: c.total_cost,
        }
      })));
      setSpecs({ performance: p.specs||[], sections: deriveDetailSections(p) });
      setPricing({ consumer_price: String(p.consumer_price||''), price_end_user: String(p.price_end_user||''), price_distributor: String(p.price_distributor||''), price_partner: String(p.price_partner||'') });
      setPriceOverride(!!p.price_override);
      setTotalCost(p.total_cost||0);
    });
  }, [editId]);

  // 원가 계산: Hardware 는 BOM 합, Appliance 는 자식 total_cost 합산 × quantity, 그 외 0
  useEffect(() => {
    if (productType === 'hardware') {
      setTotalCost(bom.reduce((sum, b) => sum + (parseInt(b.purchase_price)||0) * (parseInt(b.quantity)||1), 0));
    } else if (productType === 'appliance') {
      setTotalCost(components.reduce((sum, c) => sum + (Number(c._meta?.total_cost||0)) * (parseInt(c.quantity)||1), 0));
    } else {
      setTotalCost(0);
    }
  }, [bom, components, productType]);

  // Appliance 자식 합산가
  const applianceSum = useMemo(() => {
    if (productType !== 'appliance') return null;
    let consumer = 0, distributor = 0, partner = 0, endUser = 0;
    for (const c of components) {
      const m = c._meta || {};
      const q = parseInt(c.quantity) || 1;
      consumer    += Number(m.consumer_price    || 0) * q;
      distributor += Number(m.price_distributor || 0) * q;
      partner     += Number(m.price_partner     || 0) * q;
      endUser     += Number(m.price_end_user    || 0) * q;
    }
    return { consumer, distributor, partner, endUser };
  }, [components, productType]);

  // Appliance + 덮어쓰기 OFF 시 자식 합산을 자동으로 pricing 채움
  useEffect(() => {
    if (productType === 'appliance' && !priceOverride && applianceSum) {
      setPricing({
        consumer_price: String(applianceSum.consumer),
        price_distributor: String(applianceSum.distributor),
        price_partner: String(applianceSum.partner),
        price_end_user: String(applianceSum.endUser),
      });
    }
  }, [applianceSum, productType, priceOverride]);

  const marginRate = (price) => {
    const p = parseNumber(price);
    return totalCost > 0 && p > 0 ? (((p - totalCost) / p) * 100).toFixed(1) : '-';
  };

  // BOM 편집
  const addPartToBom = (part) => {
    if (bom.find(b => b.part_id === part.id)) return;
    setBom([...bom, { part_id: part.id, part_code: part.code, part_type: part.part_type, part_name: part.name, part_spec: part.spec, part_spec_display: part.spec_display, quantity: 1, show_in_spec: false, purchase_price: part.purchase_price }]);
    setShowAddPart(false); setPartSearch('');
  };
  const removePart = (idx) => setBom(bom.filter((_,i) => i !== idx));
  const updateBom = (idx, field, val) => setBom(bom.map((b,i) => i === idx ? {...b, [field]: val} : b));

  const [dragIdx, setDragIdx] = useState(null);
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const newBom = [...bom];
    const [moved] = newBom.splice(dragIdx, 1);
    newBom.splice(idx, 0, moved);
    setBom(newBom);
    setDragIdx(null);
  };

  // Component (자식 상품) 편집
  const addComponent = (prod) => {
    if (components.find(c => c.child_product_id === prod.id)) return;
    setComponents([...components, {
      child_product_id: prod.id,
      quantity: 1,
      _meta: {
        name: prod.name, model: prod.model, alias: prod.alias, product_type: prod.product_type,
        main_title: prod.main_title, sub_description: prod.sub_description, sw_name: prod.sw_name,
        consumer_price: prod.consumer_price,
        price_distributor: prod.price_distributor,
        price_partner: prod.price_partner,
        price_end_user: prod.price_end_user,
        total_cost: prod.total_cost,
      }
    }]);
    setShowAddComponent(false); setCompSearch('');
  };
  const removeComponent = (idx) => setComponents(components.filter((_,i) => i !== idx));
  const updateComponent = (idx, q) => setComponents(components.map((c,i) => i === idx ? {...c, quantity: parseInt(q)||1} : c));

  // 자식 후보 필터 (Appliance 가 또 Appliance 를 자식으로 가질 수 없음 + 자기 자신 제외)
  const componentCandidates = useMemo(() => {
    return allProducts.filter(p => {
      if (p.product_type === 'appliance') return false;
      if (isEdit && String(p.id) === String(editId)) return false;
      if (compTypeFilter !== 'all' && p.product_type !== compTypeFilter) return false;
      if (!matchAnyCI(compSearch, p.name, p.model, p.alias, p.category)) return false;
      return true;
    });
  }, [allProducts, compTypeFilter, compSearch, isEdit, editId]);

  // 저장
  const handleSave = async () => {
    if (!info.name) { alert('상품명은 필수입니다.'); return; }
    if (productType === 'appliance' && components.length === 0) {
      if (!window.confirm('Appliance 에 자식 상품이 없습니다. 그대로 저장할까요?')) return;
    }
    setSaving(true);
    try {
      // 빈 섹션 제거 (title/content 모두 비어있으면 제외)
      const cleanedSections = (specs.sections || []).filter(s => (s.title || '').trim() || (s.content || '').trim());
      const body = {
        product_type: productType,
        category: info.category, name: info.name, model: info.model, alias: info.alias, status: info.status,
        // 레거시 필드는 빈값으로 보내서 신규 저장에선 sections 로만 표현 (기존 값 보존을 위해 COALESCE)
        main_title: '', sub_description: '', sw_name: '',
        specs: specs.performance, features: [],
        detail_sections: cleanedSections,
      };
      // 가격: Appliance + 자동 합산이면 body 에 가격 비움. 덮어쓰기 또는 다른 유형이면 명시.
      if (productType === 'appliance' && !priceOverride) {
        body.price_override = false;
      } else {
        body.consumer_price = parseNumber(pricing.consumer_price);
        body.price_end_user = parseNumber(pricing.price_end_user);
        body.price_distributor = parseNumber(pricing.price_distributor);
        body.price_partner = parseNumber(pricing.price_partner);
        body.price_override = productType === 'appliance' ? true : false;
      }
      // 유형별 데이터
      if (productType === 'hardware') {
        body.bom = bom.map(b => ({ part_id: b.part_id, quantity: parseInt(b.quantity)||1, show_in_spec: b.show_in_spec }));
      }
      if (productType === 'appliance') {
        body.components = components.map(c => ({ child_product_id: c.child_product_id, quantity: parseInt(c.quantity)||1 }));
      }

      if (isEdit) {
        await api.put(`/products/${editId}`, body);
      } else {
        await api.post('/products', body);
      }
      navigate('/products');
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const filteredParts = allParts.filter(p => matchAnyCI(partSearch, p.name, p.code, p.spec));
  const hwParts = bom.filter(b => b.show_in_spec);

  const showComposition = productType === 'hardware' || productType === 'appliance';
  const showSpecsCard   = true; // 모든 유형에 [세부사양] 노출 (Appliance 는 요약 표시)
  const showPerformance = productType === 'hardware';
  const showHwAuto      = productType === 'hardware';

  return (
    <div>
      {/* 상단바 */}
      <div className="card" style={{ padding:'12px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span className="badge badge-gray">{isEdit ? '편집' : '신규'}</span>
          <span className={`badge ${TYPE_BADGE[productType]}`}>{TYPE_LABEL[productType]}</span>
          {!isEdit && <span className="text-muted">상품코드는 자동 부여됩니다</span>}
        </div>
        <div className="btn-group">
          <button className="btn btn-outline" onClick={() => navigate('/products')}>취소</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '저장 중...' : isEdit ? '저장' : '상품 등록'}</button>
        </div>
      </div>

      {/* 유형 선택 */}
      <div className="card">
        <div className="card-title" style={{ marginBottom:10 }}>상품 유형</div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {['software','license','hardware','appliance'].map(t => (
            <label key={t} style={{
              display:'flex', alignItems:'center', gap:8, padding:'10px 16px',
              border: productType===t ? '2px solid #3b82f6' : '1px solid #e2e8f0',
              borderRadius:8, cursor:'pointer',
              background: productType===t ? '#eff6ff' : '#fff', fontSize:13, fontWeight:600,
            }}>
              <input type="radio" name="product_type" value={t} checked={productType===t} onChange={()=>setProductType(t)} />
              <span className={`badge ${TYPE_BADGE[t]}`}>{TYPE_LABEL[t]}</span>
            </label>
          ))}
        </div>
        <div className="text-muted" style={{ marginTop:8, fontSize:11 }}>
          유형 변경 시 입력 카드가 달라지며, 다른 유형의 입력값은 저장되지 않습니다.
        </div>
      </div>

      {/* 기본정보 */}
      <div className="card">
        <div className="card-title" style={{ marginBottom:12 }}>기본 정보</div>
        <div className="form-row">
          <div className="form-group" style={{ flex:1 }}>
            <label className="form-label">상품구분</label>
            <select className="form-input" value={info.category} onChange={e => setInfo({...info, category:e.target.value})}>
              <option value="">선택</option>{categories.map(c => <option key={c.id} value={c.label}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex:1 }}><label className="form-label">상품명 *</label><input className="form-input" value={info.name} onChange={e => setInfo({...info, name:e.target.value})} /></div>
          <div className="form-group" style={{ flex:1 }}><label className="form-label">모델명</label><input className="form-input" value={info.model} onChange={e => setInfo({...info, model:e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{ flex:1 }}><label className="form-label">별칭</label><input className="form-input" value={info.alias} onChange={e => setInfo({...info, alias:e.target.value})} placeholder="예: 국내향" /></div>
          <div className="form-group" style={{ flex:1 }}>
            <label className="form-label">상태</label>
            <select className="form-input" value={info.status} onChange={e => setInfo({...info, status:e.target.value})}>
              {statuses.map(s => <option key={s.id} value={s.code}>{s.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* 상품 구성 (Hardware = BOM, Appliance = 자식 상품) */}
      {showComposition && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">상품 구성</div>
            {productType === 'hardware' ? (
              <button className="btn btn-outline btn-sm" onClick={() => setShowAddPart(true)}>+ 부품 추가</button>
            ) : (
              <button className="btn btn-outline btn-sm" onClick={() => setShowAddComponent(true)}>+ 상품 추가</button>
            )}
          </div>

          {productType === 'hardware' && (
            <table>
              <thead><tr><th style={{width:30}}></th><th>자재종류</th><th>자재명</th><th>규격(견적서명시)</th><th style={{textAlign:'right'}}>매입가</th><th style={{textAlign:'center',width:60}}>수량</th><th style={{textAlign:'right'}}>소계</th><th style={{textAlign:'center',width:70}}>사양표시</th><th style={{width:40}}></th></tr></thead>
              <tbody>
                {bom.map((b, idx) => (
                  <tr key={b.part_id} draggable onDragStart={()=>handleDragStart(idx)} onDragOver={handleDragOver} onDrop={()=>handleDrop(idx)} style={dragIdx===idx?{opacity:0.4}:{}}>
                    <td style={{cursor:'grab',textAlign:'center',color:'#94a3b8'}}>☰</td>
                    <td>{b.part_type}</td>
                    <td>{b.part_name}</td>
                    <td>{b.part_spec_display || b.part_spec}</td>
                    <td style={{ textAlign:'right' }}>₩{Number(b.purchase_price||0).toLocaleString()}</td>
                    <td style={{ textAlign:'center' }}><input style={{ width:50, padding:'3px 6px', border:'1px solid #d1d5db', borderRadius:6, textAlign:'center', fontSize:13 }} value={b.quantity} onChange={e => updateBom(idx, 'quantity', e.target.value)} /></td>
                    <td style={{ textAlign:'right', fontWeight:700 }}>₩{((parseInt(b.purchase_price)||0) * (parseInt(b.quantity)||1)).toLocaleString()}</td>
                    <td style={{ textAlign:'center' }}><input type="checkbox" checked={b.show_in_spec} onChange={e => updateBom(idx, 'show_in_spec', e.target.checked)} /></td>
                    <td><span style={{ color:'#ef4444', cursor:'pointer', fontSize:15 }} onClick={() => removePart(idx)}>✕</span></td>
                  </tr>
                ))}
                {bom.length === 0 && <tr><td colSpan={9} className="text-muted" style={{ textAlign:'center', padding:20, color:'#94a3b8' }}>부품을 추가하세요.</td></tr>}
              </tbody>
              <tfoot><tr style={{ borderTop:'2px solid #1e293b' }}>
                <td colSpan={6} style={{ textAlign:'right', fontWeight:700 }}>원가 합계</td>
                <td style={{ textAlign:'right', fontWeight:800, fontSize:15 }}>₩{totalCost.toLocaleString()}</td>
                <td colSpan={2}></td>
              </tr></tfoot>
            </table>
          )}

          {productType === 'appliance' && (
            <table>
              <thead><tr><th style={{width:80}}>유형</th><th>상품명</th><th>모델명</th><th>별칭</th><th style={{textAlign:'right'}}>소비자단가</th><th style={{textAlign:'right'}}>원가</th><th style={{textAlign:'center',width:60}}>수량</th><th style={{width:40}}></th></tr></thead>
              <tbody>
                {components.map((c, idx) => (
                  <tr key={c.child_product_id}>
                    <td><span className={`badge ${TYPE_BADGE[c._meta?.product_type]}`}>{TYPE_LABEL[c._meta?.product_type]}</span></td>
                    <td className="fw-bold">{c._meta?.name}</td>
                    <td>{c._meta?.model}</td>
                    <td>{c._meta?.alias || '-'}</td>
                    <td style={{ textAlign:'right' }}>₩{Number(c._meta?.consumer_price||0).toLocaleString()}</td>
                    <td style={{ textAlign:'right' }}>₩{Number(c._meta?.total_cost||0).toLocaleString()}</td>
                    <td style={{ textAlign:'center' }}><input style={{ width:50, padding:'3px 6px', border:'1px solid #d1d5db', borderRadius:6, textAlign:'center', fontSize:13 }} value={c.quantity} onChange={e => updateComponent(idx, e.target.value)} /></td>
                    <td><span style={{ color:'#ef4444', cursor:'pointer', fontSize:15 }} onClick={() => removeComponent(idx)}>✕</span></td>
                  </tr>
                ))}
                {components.length === 0 && <tr><td colSpan={8} className="text-muted" style={{ textAlign:'center', padding:20, color:'#94a3b8' }}>SW · License · HW 중에서 자식 상품을 선택하세요.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 세부사양 */}
      {showSpecsCard && (
        <div className="card">
          <div className="card-title" style={{ marginBottom:12 }}>세부사양</div>

          {productType === 'appliance' ? (
            <div style={{ fontSize:13, lineHeight:1.9 }}>
              <div className="text-muted" style={{ marginBottom:8, fontSize:11 }}>Appliance 는 자식 상품의 세부사양이 자동으로 합쳐져 표시됩니다.</div>
              {components.length === 0 ? <div className="text-muted">자식 상품을 추가하세요.</div> : (
                components.map((c, i) => (
                  <div key={i} style={{ padding:'10px 12px', background:'#f8fafc', borderRadius:8, marginBottom:8 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
                      <span className={`badge ${TYPE_BADGE[c._meta?.product_type]}`}>{TYPE_LABEL[c._meta?.product_type]}</span>
                      <span style={{ fontWeight:700 }}>{c._meta?.name}</span>
                      {c._meta?.model && <span style={{ color:'#64748b' }}>{c._meta.model}</span>}
                    </div>
                    {c._meta?.main_title && <div style={{ fontSize:12, fontWeight:600 }}>{c._meta.main_title}</div>}
                    {c._meta?.sub_description && <div style={{ fontSize:11, color:'#64748b', whiteSpace:'pre-wrap', marginTop:2 }}>{c._meta.sub_description}</div>}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div style={{ display:'flex', gap:28 }}>
              <div style={{ flex:1 }}>
                {/* 자유 입력 세부사양 — 제목(볼드)+내용(일반) 리스트 */}
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>세부사양 <span className="text-muted" style={{fontSize:11,fontWeight:400}}>(제목은 볼드, 내용은 일반체로 견적서에 표시)</span></div>
                  {(specs.sections||[]).map((s,i) => (
                    <div key={i} style={{ display:'flex', gap:6, marginBottom:6, alignItems:'flex-start' }}>
                      <div style={{flex:1,display:'flex',flexDirection:'column',gap:4}}>
                        <input className="form-input" style={{padding:'5px 8px',fontWeight:700}} value={s.title||''}
                          onChange={e=>{ const u=[...specs.sections]; u[i]={...u[i],title:e.target.value}; setSpecs({...specs, sections:u}); }}
                          placeholder="제목 (볼드)" />
                        <textarea className="form-input" rows={2} style={{padding:'5px 8px'}} value={s.content||''}
                          onChange={e=>{ const u=[...specs.sections]; u[i]={...u[i],content:e.target.value}; setSpecs({...specs, sections:u}); }}
                          placeholder="내용 (일반)" />
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:2}}>
                        <button className="btn btn-outline btn-sm" style={{padding:'2px 8px'}} disabled={i===0}
                          onClick={()=>{ const u=[...specs.sections]; [u[i-1],u[i]]=[u[i],u[i-1]]; setSpecs({...specs, sections:u}); }}>↑</button>
                        <button className="btn btn-outline btn-sm" style={{padding:'2px 8px'}} disabled={i===(specs.sections.length-1)}
                          onClick={()=>{ const u=[...specs.sections]; [u[i+1],u[i]]=[u[i],u[i+1]]; setSpecs({...specs, sections:u}); }}>↓</button>
                        <button className="btn btn-outline btn-sm" style={{padding:'2px 8px',color:'#ef4444'}}
                          onClick={()=>setSpecs({...specs, sections:specs.sections.filter((_,j)=>j!==i)})}>✕</button>
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-outline btn-sm" onClick={()=>setSpecs({...specs, sections:[...(specs.sections||[]), {title:'',content:''}]})}>+ 항목 추가</button>
                </div>

                {showPerformance && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>성능사양</div>
                    {specs.performance.map((s,i) => (
                      <div key={i} style={{ display:'flex', gap:8, marginBottom:6, alignItems:'center' }}>
                        <input className="form-input" style={{ flex:1, padding:'5px 8px' }} value={s.key} onChange={e => { const u=[...specs.performance]; u[i]={...u[i],key:e.target.value}; setSpecs({...specs, performance:u}); }} placeholder="항목" />
                        <input className="form-input" style={{ flex:1, padding:'5px 8px' }} value={s.value} onChange={e => { const u=[...specs.performance]; u[i]={...u[i],value:e.target.value}; setSpecs({...specs, performance:u}); }} placeholder="값" />
                        <span style={{ color:'#ef4444', cursor:'pointer' }} onClick={() => setSpecs({...specs, performance:specs.performance.filter((_,j)=>j!==i)})}>✕</span>
                      </div>
                    ))}
                    <button className="btn btn-outline btn-sm" onClick={() => setSpecs({...specs, performance:[...specs.performance, {key:'',value:''}]})}>+ 항목 추가</button>
                  </div>
                )}
              </div>
              <div style={{ flex:1 }}>
                {showHwAuto && (
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>하드웨어사양 <span className="text-muted">(사양표시 부품 자동반영)</span></div>
                    <div style={{ fontSize:13, lineHeight:2 }}>
                      {hwParts.length > 0 ? hwParts.map((b,i) => <div key={i}><span style={{ color:'#64748b', display:'inline-block', width:80 }}>{b.part_type}</span>{b.part_spec_display || b.part_spec}</div>) : <span className="text-muted">사양표시 체크된 부품이 없습니다.</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 원가 + 가격 */}
      <div className="grid-2">
        <div className="card">
          <div className="card-title" style={{ marginBottom:10 }}>원가</div>
          <div style={{ fontSize:24, fontWeight:800 }}>₩{totalCost.toLocaleString()}</div>
          <div className="text-muted" style={{ marginTop:8 }}>
            {productType === 'hardware' && '* BOM 합산 (Active 원가기준표 기준)'}
            {productType === 'appliance' && '* 자식 상품의 BOM 원가 합산 × 수량'}
            {(productType === 'software' || productType === 'license') && '* 이 유형은 원가가 0 입니다.'}
          </div>
        </div>
        <div className="card">
          <div className="card-header" style={{ marginBottom:10 }}>
            <div className="card-title">가격 설정</div>
            {productType === 'appliance' && (
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer' }}>
                <input type="checkbox" checked={priceOverride} onChange={e => setPriceOverride(e.target.checked)} />
                자식 합산 덮어쓰기
              </label>
            )}
          </div>
          {productType === 'appliance' && !priceOverride && (
            <div className="alert alert-info" style={{ marginBottom:12, fontSize:12, padding:'8px 12px' }}>
              ℹ 자식 상품의 가격을 자동 합산해 저장합니다. 직접 입력하려면 "덮어쓰기" 를 켜세요.
            </div>
          )}
          <div className="form-group">
            <label className="form-label">소비자단가 {productType!=='appliance' || priceOverride ? '(필수)' : ''}</label>
            <input className="form-input" style={{ textAlign:'right' }} disabled={productType==='appliance' && !priceOverride} value={formatNumber(pricing.consumer_price)} onChange={e => setPricing({...pricing, consumer_price:String(parseNumber(e.target.value))})} />
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex:1 }}>
              <label className="form-label">총판(Distributor)</label>
              <input className="form-input" style={{ textAlign:'right' }} disabled={productType==='appliance' && !priceOverride} value={formatNumber(pricing.price_distributor)} onChange={e => setPricing({...pricing, price_distributor:String(parseNumber(e.target.value))})} />
              <div className="text-muted" style={{ marginTop:4 }}>마진율: <strong style={{ color:'#22c55e' }}>{marginRate(pricing.price_distributor)}%</strong></div>
            </div>
            <div className="form-group" style={{ flex:1 }}>
              <label className="form-label">협력사(Partner)</label>
              <input className="form-input" style={{ textAlign:'right' }} disabled={productType==='appliance' && !priceOverride} value={formatNumber(pricing.price_partner)} onChange={e => setPricing({...pricing, price_partner:String(parseNumber(e.target.value))})} />
              <div className="text-muted" style={{ marginTop:4 }}>마진율: <strong style={{ color:'#22c55e' }}>{marginRate(pricing.price_partner)}%</strong></div>
            </div>
            <div className="form-group" style={{ flex:1 }}>
              <label className="form-label">최종사용자(End User)</label>
              <input className="form-input" style={{ textAlign:'right' }} disabled={productType==='appliance' && !priceOverride} value={formatNumber(pricing.price_end_user)} onChange={e => setPricing({...pricing, price_end_user:String(parseNumber(e.target.value))})} />
              <div className="text-muted" style={{ marginTop:4 }}>마진율: <strong style={{ color:'#22c55e' }}>{marginRate(pricing.price_end_user)}%</strong></div>
            </div>
          </div>
        </div>
      </div>

      {/* 부품 추가 모달 (Hardware) */}
      {showAddPart && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setShowAddPart(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:28, width:1000, maxWidth:'92vw', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>부품 추가</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowAddPart(false)}>✕</span>
            </div>
            <div className="form-group"><input className="form-input" autoFocus value={partSearch} onChange={e => setPartSearch(e.target.value)} placeholder="부품명, 코드, 규격 검색..." /></div>
            <div style={{ maxHeight:400, overflowY:'auto' }}>
              <table style={{ fontSize:12 }}>
                <thead><tr><th>자재종류</th><th>자재명</th><th>규격(견적서명시)</th><th style={{textAlign:'right'}}>매입가</th><th></th></tr></thead>
                <tbody>
                  {filteredParts.map(p => {
                    const already = bom.find(b => b.part_id === p.id);
                    return (
                      <tr key={p.id} style={already ? { opacity:0.4 } : {}}>
                        <td>{p.part_type}</td>
                        <td>{p.name}</td>
                        <td>{p.spec_display || p.spec}</td>
                        <td style={{ textAlign:'right' }}>₩{Number(p.purchase_price||0).toLocaleString()}</td>
                        <td>{!already && <button className="btn btn-primary btn-sm" onClick={() => addPartToBom(p)}>추가</button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 자식 상품 추가 모달 (Appliance) */}
      {showAddComponent && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setShowAddComponent(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:28, width:1000, maxWidth:'92vw', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>자식 상품 추가</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowAddComponent(false)}>✕</span>
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
              <input className="form-input" style={{ flex:1 }} autoFocus value={compSearch} onChange={e => setCompSearch(e.target.value)} placeholder="이름·모델·별칭·구분 검색..." />
              {['all','software','license','hardware'].map(t => (
                <button key={t} className={`btn btn-sm ${compTypeFilter===t?'btn-primary':'btn-outline'}`} onClick={()=>setCompTypeFilter(t)}>
                  {t==='all' ? '전체' : TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            <div style={{ maxHeight:400, overflowY:'auto' }}>
              <table style={{ fontSize:12 }}>
                <thead><tr><th style={{width:80}}>유형</th><th>상품구분</th><th>상품명</th><th>모델명</th><th>별칭</th><th style={{textAlign:'right'}}>원가합계</th><th></th></tr></thead>
                <tbody>
                  {componentCandidates.map(p => {
                    const already = components.find(c => c.child_product_id === p.id);
                    return (
                      <tr key={p.id} style={already ? { opacity:0.4 } : {}}>
                        <td><span className={`badge ${TYPE_BADGE[p.product_type]}`}>{TYPE_LABEL[p.product_type]}</span></td>
                        <td><span className="badge badge-gray">{p.category}</span></td>
                        <td className="fw-bold">{p.name}</td>
                        <td>{p.model}</td>
                        <td>{p.alias || '-'}</td>
                        <td style={{ textAlign:'right' }}>₩{Number(p.total_cost||0).toLocaleString()}</td>
                        <td>{!already && <button className="btn btn-primary btn-sm" onClick={() => addComponent(p)}>추가</button>}</td>
                      </tr>
                    );
                  })}
                  {componentCandidates.length === 0 && (() => {
                    const eligibleTotal = allProducts.filter(p => p.product_type !== 'appliance' && !(isEdit && String(p.id) === String(editId))).length;
                    return (
                      <tr><td colSpan={7} style={{ textAlign:'center', padding:20, color:'#94a3b8' }}>
                        {allProducts.length === 0
                          ? '상품 데이터를 불러오지 못했습니다. DB 마이그레이션이 적용되었는지 확인하세요.'
                          : eligibleTotal === 0
                            ? `SW / License / Hardware 유형의 상품이 한 개도 없습니다. (전체 ${allProducts.length}개는 모두 Appliance) 상품 관리에서 유형을 재분류하거나 새로 등록하세요.`
                            : '검색·필터 조건에 맞는 상품이 없습니다.'}
                      </td></tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
